import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

// Action Types
export const FETCH_WAREHOUSES = 'FETCH_WAREHOUSES';
export const FETCH_STOCK_ITEMS = 'FETCH_STOCK_ITEMS';
export const FETCH_STOCK_CATEGORIES = 'FETCH_STOCK_CATEGORIES';
export const FETCH_STOCK_MOVEMENTS = 'FETCH_STOCK_MOVEMENTS';
export const FETCH_STOCK_TRANSFERS = 'FETCH_STOCK_TRANSFERS';
export const FETCH_STOCK_ALERTS = 'FETCH_STOCK_ALERTS';
export const SET_LOADING = 'SET_LOADING';
export const SET_ERROR = 'SET_ERROR';

// Get Auth Token
const getAuthToken = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?.token;
};

const getAuthHeaders = () => ({
    headers: {
        Authorization: `Bearer ${getAuthToken()}`,
    },
});

// Warehouse Actions
export const fetchWarehouses = (params = {}) => async (dispatch) => {
    dispatch({ type: SET_LOADING, payload: true });
    try {
        const response = await axios.get(
            `${API_BASE_URL}/business/stock/warehouse/all`,
            { ...getAuthHeaders(), params }
        );
        dispatch({ type: FETCH_WAREHOUSES, payload: response.data });
    } catch (error) {
        dispatch({ type: SET_ERROR, payload: error.response?.data?.message || 'Error fetching warehouses' });
    } finally {
        dispatch({ type: SET_LOADING, payload: false });
    }
};

export const addWarehouse = (warehouseData) => async () => {
    const response = await axios.post(
        `${API_BASE_URL}/business/stock/warehouse/add`,
        warehouseData,
        getAuthHeaders()
    );
    return response.data;
};

export const updateWarehouse = (id, warehouseData) => async () => {
    const response = await axios.post(
        `${API_BASE_URL}/business/stock/warehouse/update/${id}`,
        warehouseData,
        getAuthHeaders()
    );
    return response.data;
};

export const deleteWarehouse = (id) => async () => {
    const response = await axios.delete(
        `${API_BASE_URL}/business/stock/warehouse/delete/${id}`,
        getAuthHeaders()
    );
    return response.data;
};

// Stock Item Actions
export const fetchStockItems = (params = {}) => async (dispatch) => {
    dispatch({ type: SET_LOADING, payload: true });
    try {
        const response = await axios.get(
            `${API_BASE_URL}/business/stock/item/all`,
            { ...getAuthHeaders(), params }
        );
        dispatch({ type: FETCH_STOCK_ITEMS, payload: response.data });
    } catch (error) {
        dispatch({ type: SET_ERROR, payload: error.response?.data?.message || 'Error fetching stock items' });
    } finally {
        dispatch({ type: SET_LOADING, payload: false });
    }
};

export const addStockItem = (itemData) => async () => {
    const response = await axios.post(
        `${API_BASE_URL}/business/stock/item/add`,
        itemData,
        getAuthHeaders()
    );
    return response.data;
};

export const updateStockItem = (id, itemData) => async () => {
    const response = await axios.post(
        `${API_BASE_URL}/business/stock/item/update/${id}`,
        itemData,
        getAuthHeaders()
    );
    return response.data;
};

export const deleteStockItem = (id) => async () => {
    const response = await axios.delete(
        `${API_BASE_URL}/business/stock/item/delete/${id}`,
        getAuthHeaders()
    );
    return response.data;
};

// Category Actions
export const fetchStockCategories = (params = {}) => async (dispatch) => {
    dispatch({ type: SET_LOADING, payload: true });
    try {
        const response = await axios.get(
            `${API_BASE_URL}/business/stock/category/all`,
            { ...getAuthHeaders(), params }
        );
        dispatch({ type: FETCH_STOCK_CATEGORIES, payload: response.data });
    } catch (error) {
        dispatch({ type: SET_ERROR, payload: error.response?.data?.message || 'Error fetching categories' });
    } finally {
        dispatch({ type: SET_LOADING, payload: false });
    }
};

export const addStockCategory = (categoryData) => async () => {
    const response = await axios.post(
        `${API_BASE_URL}/business/stock/category/add`,
        categoryData,
        getAuthHeaders()
    );
    return response.data;
};

export const updateStockCategory = (id, categoryData) => async () => {
    const response = await axios.post(
        `${API_BASE_URL}/business/stock/category/update/${id}`,
        categoryData,
        getAuthHeaders()
    );
    return response.data;
};

export const deleteStockCategory = (id) => async () => {
    const response = await axios.delete(
        `${API_BASE_URL}/business/stock/category/delete/${id}`,
        getAuthHeaders()
    );
    return response.data;
};

// Movement Actions
export const fetchStockMovements = (params = {}) => async (dispatch) => {
    dispatch({ type: SET_LOADING, payload: true });
    try {
        const response = await axios.get(
            `${API_BASE_URL}/business/stock/movement/all`,
            { ...getAuthHeaders(), params }
        );
        dispatch({ type: FETCH_STOCK_MOVEMENTS, payload: response.data });
    } catch (error) {
        dispatch({ type: SET_ERROR, payload: error.response?.data?.message || 'Error fetching movements' });
    } finally {
        dispatch({ type: SET_LOADING, payload: false });
    }
};

export const addStockMovement = (movementData) => async () => {
    const response = await axios.post(
        `${API_BASE_URL}/business/stock/movement/add`,
        movementData,
        getAuthHeaders()
    );
    return response.data;
};

// Transfer Actions
export const fetchStockTransfers = (params = {}) => async (dispatch) => {
    dispatch({ type: SET_LOADING, payload: true });
    try {
        const response = await axios.get(
            `${API_BASE_URL}/business/stock/transfer/all`,
            { ...getAuthHeaders(), params }
        );
        dispatch({ type: FETCH_STOCK_TRANSFERS, payload: response.data });
    } catch (error) {
        dispatch({ type: SET_ERROR, payload: error.response?.data?.message || 'Error fetching transfers' });
    } finally {
        dispatch({ type: SET_LOADING, payload: false });
    }
};

export const addStockTransfer = (transferData) => async () => {
    const response = await axios.post(
        `${API_BASE_URL}/business/stock/transfer/add`,
        transferData,
        getAuthHeaders()
    );
    return response.data;
};

export const approveTransfer = (id) => async () => {
    const response = await axios.post(
        `${API_BASE_URL}/business/stock/transfer/approve/${id}`,
        {},
        getAuthHeaders()
    );
    return response.data;
};

export const completeTransfer = (id) => async () => {
    const response = await axios.post(
        `${API_BASE_URL}/business/stock/transfer/complete/${id}`,
        {},
        getAuthHeaders()
    );
    return response.data;
};

export const cancelTransfer = (id) => async () => {
    const response = await axios.post(
        `${API_BASE_URL}/business/stock/transfer/cancel/${id}`,
        {},
        getAuthHeaders()
    );
    return response.data;
};

// Alert Actions
export const fetchStockAlerts = (params = {}) => async (dispatch) => {
    dispatch({ type: SET_LOADING, payload: true });
    try {
        const response = await axios.get(
            `${API_BASE_URL}/business/stock/alert/all`,
            { ...getAuthHeaders(), params }
        );
        dispatch({ type: FETCH_STOCK_ALERTS, payload: response.data });
    } catch (error) {
        dispatch({ type: SET_ERROR, payload: error.response?.data?.message || 'Error fetching alerts' });
    } finally {
        dispatch({ type: SET_LOADING, payload: false });
    }
};

export const markAlertAsRead = (id) => async () => {
    const response = await axios.post(
        `${API_BASE_URL}/business/stock/alert/mark-read/${id}`,
        {},
        getAuthHeaders()
    );
    return response.data;
};





